/**
 * Passive animals: pig, cow, sheep, chicken. They stroll with idle pauses, look
 * at a nearby player, avoid water and ledges, and panic (flee) when hurt.
 * Drops come out cooked if the animal dies burning, like vanilla.
 */

import { COLOR_KEYS, GRASS_ID, WOOL_IDS } from '../blocks';
import { I } from '../game/itemIds';
import { stackOf, type Stack } from '../game/types';
import { Mob, type MobCtx, type MobSpec } from './mob';
import { isBreedFood } from './breeding';
import { effectiveLight, type EntityHost } from './host';

/** Vanilla AgeableMob: babies take 20 min (24000 ticks) to grow up. */
export const BABY_GROW_TICKS = 24000;
/** Love mode lasts 30 s (600 ticks). */
export const LOVE_TICKS = 600;
/** After breeding, adults wait 5 min (6000 ticks). */
export const BREED_COOLDOWN = 6000;

const roll = (rnd: () => number, lo: number, hi: number): number => lo + Math.floor(rnd() * (hi - lo + 1));

export abstract class Animal extends Mob {
  /** Flee speed multiplier. */
  protected panicMod = 1.25;
  /** Baby (entities/breeding.ts): half-size hitbox, no drops/XP, grows up after growTicks. */
  baby = false;
  growTicks = 0;
  /** Ticks of love mode left (fed its breeding item). */
  loveTicks = 0;
  /** Ticks before it can fall in love again. */
  breedCooldown = 0;
  /** Set by the breeding pass: the partner it walks to, and how long they have been close. */
  mate: Animal | null = null;
  mateTicks = 0;
  /** Babies: the adult they trail (FollowParentGoal). */
  follow: Animal | null = null;
  private adultSize: [number, number, number] | null = null;

  setBaby(baby: boolean, growTicks = BABY_GROW_TICKS): void {
    const self = this as unknown as { width: number; height: number; eyeHeight: number };
    this.adultSize ??= [this.width, this.height, this.eyeHeight];
    const k = baby ? 0.5 : 1;
    self.width = this.adultSize[0] * k; self.height = this.adultSize[1] * k; self.eyeHeight = this.adultSize[2] * k;
    this.baby = baby;
    this.view.baby = baby;
    this.growTicks = baby ? growTicks : 0;
    if (baby) { this.loveTicks = 0; this.mate = null; } else this.follow = null;
  }

  /** Can be fed into love mode right now (vanilla canFallInLove). */
  get canFallInLove(): boolean { return !this.baby && this.breedCooldown <= 0 && this.loveTicks <= 0; }

  override syncView(host: EntityHost): void {
    super.syncView(host);
    this.view.baby = this.baby;
  }

  /** Saved per-type state (entities/persist.ts). */
  saveExtra(): unknown {
    const o: Record<string, number> = {};
    if (this.baby) { o.b = 1; o.g = this.growTicks; }
    if (this.loveTicks > 0) o.l = this.loveTicks;
    if (this.breedCooldown > 0) o.c = this.breedCooldown;
    this.saveMore(o);
    return o;
  }
  loadExtra(d: unknown): void {
    if (Array.isArray(d)) { this.loadMore({ s: d[0], col: d[1] }); return; } // pre-breeding sheep rows
    const o = (d ?? {}) as Record<string, number>;
    if (o.b) this.setBaby(true, Math.max(1, o.g | 0));
    this.loveTicks = o.l | 0;
    this.breedCooldown = o.c | 0;
    this.loadMore(o);
  }
  protected saveMore(_o: Record<string, number>): void {}
  protected loadMore(_o: Record<string, number>): void {}

  /** A newborn takes after its parents (panda genes, fox coat); sheep colour is handled in breeding.ts. */
  inherit(_a: Animal, _b: Animal, _rnd: () => number): void {}

  /** Babies drop nothing and give no XP (vanilla). */
  protected noDrops(): boolean { return this.baby; }

  /** Growing up, love hearts, cooldown. */
  protected ageTick(ctx: MobCtx): void {
    if (this.baby && --this.growTicks <= 0) this.setBaby(false);
    if (this.breedCooldown > 0) this.breedCooldown--;
    if (this.loveTicks > 0) {
      this.loveTicks--;
      if (this.loveTicks % 10 === 0) {
        const r = ctx.host.rnd;
        ctx.host.effect('heart', this.x + (r() - 0.5) * this.width * 2, this.y + 0.5 + r() * this.height, this.z + (r() - 0.5) * this.width * 2);
      }
      if (this.loveTicks === 0) this.mate = null;
    }
  }

  constructor(id: number, type: 'pig' | 'cow' | 'sheep' | 'chicken' | 'horse' | 'donkey' | 'panda' | 'fox', x: number, y: number, z: number, spec: MobSpec, rnd: () => number) {
    super(id, type, x, y, z, spec, rnd);
  }

  override onHurtBy(fromX: number, fromZ: number, rnd: () => number): void {
    this.panicTicks = 60 + Math.floor(rnd() * 40);
    // run away from the attacker, with some wobble
    this.panicAngle = Math.atan2(this.z - fromZ, this.x - fromX) + (rnd() - 0.5) * 1.2;
  }

  xpValue(rnd: () => number): number { return this.baby ? 0 : roll(rnd, 1, 3); }

  /** Vanilla Animal.getWalkTargetValue: grass 10, else brighter is better. */
  protected override walkValue(x: number, y: number, z: number): number {
    return this.w.getBlock(x, y - 1, z) === GRASS_ID ? 10 : effectiveLight(this.host, x, y, z) - 0.5;
  }

  protected ai(ctx: MobCtx): void {
    this.ageTick(ctx);
    if (this.panicTicks > 0) {
      this.panicTicks--;
      if (this.panicTicks % 20 === 0) this.panicAngle += (ctx.host.rnd() - 0.5) * 1.5;
      const dx = Math.cos(this.panicAngle), dz = Math.sin(this.panicAngle);
      // run to a spot on the far side of the attacker, around obstacles
      if (this.navAway(ctx, this.x - dx * 8, this.z - dz * 8, this.panicMod, 3, true)) return;
      if (this.safeAhead(dx, dz, 3, true)) this.setMove(dx, dz, this.panicMod);
      else this.panicAngle += Math.PI * 0.5 + ctx.host.rnd();
      return;
    }
    // BreedGoal: walk to the partner; FollowParentGoal: babies trail an adult
    const goal = this.mate?.alive ? this.mate : this.follow?.alive ? this.follow : null;
    if (goal) {
      const dx = goal.x - this.x, dz = goal.z - this.z;
      const keep = goal === this.mate ? 1 : 3;
      if (dx * dx + dz * dz > keep * keep) {
        const mod = goal === this.mate ? 1 : 1.1;
        if (!this.navTo(ctx, goal.x, goal.y, goal.z, mod, 3, true, 16, 150)) this.setMove(dx, dz, mod);
        this.faceToward(goal.x, goal.z);
      }
      this.lookAt(goal.x, goal.y + goal.eyeHeight, goal.z);
      return;
    }
    if (this.tempted(ctx)) return;
    this.stroll(ctx, 1, 3, true);
    this.lookAtNearPlayer(ctx);
  }

  /** Vanilla TemptGoal: follow a player holding breeding food within 10 blocks; 5 s off after losing interest. */
  private temptCd = 0;
  private temptOn = false;
  private tempted(ctx: MobCtx): boolean {
    if (this.temptCd > 0) this.temptCd--;
    const p = ctx.host.player, held = ctx.host.heldItem?.() ?? 0;
    const dx = p.x - this.x, dz = p.z - this.z, d2 = dx * dx + dz * dz;
    const want = this.temptCd === 0 && held !== 0 && ctx.targetable() && d2 < 100 && isBreedFood(this.type, held);
    if (!want) {
      if (this.temptOn) { this.temptOn = false; this.temptCd = 100; }
      return false;
    }
    this.temptOn = true;
    this.lookAt(p.x, p.eyeY, p.z);
    this.faceToward(p.x, p.z);
    if (d2 > 2.5 * 2.5) {
      if (!this.navTo(ctx, p.x, p.y, p.z, 1, 3, true, 16, 150) && this.safeAhead(dx, dz, 3, true)) this.setMove(dx, dz, 1);
    }
    return true;
  }

  protected abstract raw: number;
  protected abstract cooked: number;
  /** Meat drop count range. */
  protected meat(rnd: () => number, burning: boolean): Stack[] {
    const n = this.meatCount(rnd);
    return n > 0 ? [stackOf(burning ? this.cooked : this.raw, n)] : [];
  }
  protected meatCount(rnd: () => number): number { return roll(rnd, 1, 3); }
}

export class Pig extends Animal {
  protected raw = I.PORKCHOP;
  protected cooked = I.COOKED_PORKCHOP;
  constructor(id: number, x: number, y: number, z: number, rnd: () => number) {
    super(id, 'pig', x, y, z, { width: 0.9, height: 0.9, eye: 0.6, health: 10, speed: 0.25 }, rnd);
  }
  loot(rnd: () => number): Stack[] {
    const out = this.noDrops() ? [] : this.meat(rnd, this.fireTicks > 0);
    if (this.saddled) out.push(stackOf(I.SADDLE, 1));
    return out;
  }
  protected override saveMore(o: Record<string, number>): void { if (this.saddled) o.sd = 1; }
  protected override loadMore(o: Record<string, number>): void { this.saddled = !!o.sd; }
}

export class Cow extends Animal {
  protected raw = I.BEEF;
  protected cooked = I.COOKED_BEEF;
  constructor(id: number, x: number, y: number, z: number, rnd: () => number) {
    super(id, 'cow', x, y, z, { width: 0.9, height: 1.4, eye: 1.3, health: 10, speed: 0.2 }, rnd);
    this.panicMod = 2;
  }
  loot(rnd: () => number): Stack[] {
    if (this.noDrops()) return [];
    const out = this.meat(rnd, this.fireTicks > 0);
    const l = roll(rnd, 0, 2);
    if (l > 0) out.push(stackOf(I.LEATHER, l));
    return out;
  }
}

/** Vanilla natural sheep colours: white 81.8%, black/gray/light gray 5% each, brown 3%, pink 0.16%. */
export function randomSheepColor(rnd: () => number): number {
  const r = rnd() * 100;
  if (r < 5) return 15;          // black
  if (r < 10) return 7;          // gray
  if (r < 15) return 8;          // light gray
  if (r < 18) return 12;         // brown
  if (r < 18.164) return 6;      // pink
  return 0;                      // white
}

export class Sheep extends Animal {
  protected raw = I.MUTTON;
  protected cooked = I.COOKED_MUTTON;
  constructor(id: number, x: number, y: number, z: number, rnd: () => number) {
    super(id, 'sheep', x, y, z, { width: 0.9, height: 1.3, eye: 1.235, health: 8, speed: 0.23 }, rnd);
    this.color = randomSheepColor(rnd);
  }
  protected override meatCount(rnd: () => number): number { return roll(rnd, 1, 2); }
  protected override saveMore(o: Record<string, number>): void { o.s = this.sheared ? 1 : 0; o.col = this.color; }
  protected override loadMore(o: Record<string, number>): void { this.sheared = !!o.s; this.color = o.col | 0; }

  /** Shears: 1-3 wool of its colour (vanilla), only adults with wool. */
  shear(rnd: () => number): Stack[] {
    if (this.sheared || this.baby || !this.alive) return [];
    this.sheared = true;
    const id = WOOL_IDS[COLOR_KEYS[this.color]];
    return id ? [stackOf(id, roll(rnd, 1, 3))] : [];
  }

  protected override ai(ctx: MobCtx): void {
    // EatBlockGoal: now and then graze the grass underfoot (vanilla 1/1000 per
    // tick for adults, 1/50 for babies): 40 ticks head-down, and at tick 4 the
    // grass becomes dirt, the wool regrows and a baby grows up a minute sooner
    const bx = Math.floor(this.x), by = Math.floor(this.y - 0.5), bz = Math.floor(this.z);
    if (this.eatTicks > 0) {
      this.eatTicks--;
      this.ageTick(ctx);
      if (this.eatTicks === 4 && ctx.host.eatGrass?.(bx, by, bz)) {
        this.sheared = false;
        if (this.baby) this.growTicks = Math.max(1, this.growTicks - 1200);
      }
      if (this.panicTicks <= 0) return; // stands still while grazing
      this.eatTicks = 0;
    } else if (this.panicTicks <= 0 && !this.mate && this.onGround && ctx.host.rnd() < (this.baby ? 1 / 50 : 1 / 1000)
      && ctx.world.getBlock(bx, by, bz) === GRASS_ID) {
      this.eatTicks = 40;
      return;
    }
    super.ai(ctx);
  }
  /** Grazing animation ticks left (40 -> 0). */
  eatTicks = 0;
  override syncView(host: EntityHost): void {
    super.syncView(host);
    this.view.eat = this.eatTicks;
  }
  loot(rnd: () => number): Stack[] {
    if (this.noDrops()) return [];
    const out = this.meat(rnd, this.fireTicks > 0);
    if (!this.sheared) {
      const id = WOOL_IDS[COLOR_KEYS[this.color]];
      if (id) out.push(stackOf(id, 1));
    }
    return out;
  }
}

export class Chicken extends Animal {
  protected raw = I.CHICKEN;
  protected cooked = I.COOKED_CHICKEN;
  constructor(id: number, x: number, y: number, z: number, rnd: () => number) {
    super(id, 'chicken', x, y, z, { width: 0.4, height: 0.7, eye: 0.644, health: 4, speed: 0.25 }, rnd);
    this.panicMod = 1.4;
    this.noFallDamage = true;
    this.slowFall = true;
    this.eggTicks = 6000 + Math.floor(rnd() * 6000);
  }
  /** Ticks to the next laid egg (vanilla 5-10 min). */
  eggTicks: number;
  protected override meatCount(): number { return 1; }
  protected override saveMore(o: Record<string, number>): void { o.e = this.eggTicks; }
  protected override loadMore(o: Record<string, number>): void { if (o.e > 0) this.eggTicks = o.e; }
  protected override ai(ctx: MobCtx): void {
    if (!this.baby && --this.eggTicks <= 0) {
      ctx.host.dropItem(stackOf(I.EGG, 1), this.x, this.y, this.z);
      this.eggTicks = 6000 + Math.floor(ctx.host.rnd() * 6000);
    }
    super.ai(ctx);
  }
  loot(rnd: () => number): Stack[] {
    if (this.noDrops()) return [];
    const out = this.meat(rnd, this.fireTicks > 0);
    const f = roll(rnd, 0, 2);
    if (f > 0) out.push(stackOf(I.FEATHER, f));
    return out;
  }
}
